import React, { useState } from 'react';
import {
  IAuditoriaValorRedondoResultado,
  IAuditoriaValorRedondoItem,
} from '../interfaces/IAuditoriaValorRedondo';

interface Props {
  resultado: IAuditoriaValorRedondoResultado;
}

const formatCurrency = (value?: number) =>
  (value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const formatDate = (dateStr?: string) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('pt-BR');
};

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const cls =
    status === 'ERRO'   ? 'badge-light-danger' :
    status === 'ALERTA' ? 'badge-light-warning' :
    status === 'AVISO'  ? 'badge-light-warning' :
                          'badge-light-success';
  return <span className={`badge ${cls}`}>{status}</span>;
};

const ItemExpandivel: React.FC<{ item: IAuditoriaValorRedondoItem }> = ({ item }) => {
  const [expandido, setExpandido] = useState(false);

  return (
    <>
      <tr style={{ cursor: 'pointer' }} onClick={() => setExpandido(!expandido)}>
        <td>
          <span className='fw-bold'>{item.empresaNome}</span>
          <br />
          <span className='text-muted fs-8'>{item.cnpj}</span>
        </td>
        <td>{formatDate(item.dataLancamento)}</td>
        <td>
          <span className='fw-bold'>{item.contaReferencial}</span>
          <br />
          <span className='text-muted fs-8'>{item.descricaoConta}</span>
        </td>
        <td className='text-muted fs-8'>{item.historico || '—'}</td>
        <td className='fw-bold'>{formatCurrency(item.valor)}</td>
        <td><StatusBadge status={item.status} /></td>
        <td className='text-end'>{expandido ? '▲' : '▼'}</td>
      </tr>

      {expandido && (
        <tr>
          <td colSpan={7} className='bg-light-secondary p-4'>
            <div className='mb-3'>
              <strong>Diagnóstico:</strong>
              <p className='text-muted mt-1 mb-2'>{item.mensagemDiagnostico}</p>
            </div>

            {item.status === 'ERRO' && (
              <div className='row mb-4'>
                <div className='col-3 text-center border-end'>
                  <div className='fs-4 fw-bolder text-danger'>{formatCurrency(item.tributosIrpjCsll)}</div>
                  <div className='text-muted fs-8'>IRPJ/CSLL (34%)</div>
                </div>
                <div className='col-3 text-center border-end'>
                  <div className='fs-4 fw-bolder text-danger'>{formatCurrency(item.multaArt44)}</div>
                  <div className='text-muted fs-8'>Multa Art. 44 (75%)</div>
                </div>
                <div className='col-3 text-center border-end'>
                  <div className='fs-4 fw-bolder text-warning'>{formatCurrency(item.jurosSelic)}</div>
                  <div className='text-muted fs-8'>Juros SELIC</div>
                </div>
                <div className='col-3 text-center'>
                  <div className='fs-4 fw-bolder text-danger'>{formatCurrency(item.passivoTributario)}</div>
                  <div className='text-muted fs-8'>Passivo Total</div>
                </div>
              </div>
            )}

            {item.status === 'ERRO' && (
              <div className='alert alert-danger mt-3 py-2'>
                <strong>Regularização obrigatória:</strong> Lançamento redondo acima de R$ 20.000,00 em conta de
                despesa operacional. Solicite NF/Contrato que suporte o valor exato. Sem documentação, o lançamento
                pode configurar despesa indébita com omissão de receita. Recolha o passivo ou retifique o lançamento.
                Lei 6.404/76 Art. 177, Lei 9.430/96 Art. 44 I.
              </div>
            )}

            {item.status === 'ALERTA' && (
              <div className='alert alert-warning mt-3 py-2'>
                <strong>Ação recomendada:</strong> Lançamento redondo entre R$ 5.000,01 e R$ 20.000,00.
                Suspeita de registro por estimativa. Solicite documento suporte (NF, contrato, boleto).
                Resolução CFC nº 1.330/2011 (ITG 2000).
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
};

const AuditoriaValorRedondoDetalhe: React.FC<Props> = ({ resultado }) => {
  const bannerCor =
    resultado.status === 'ERRO'   ? 'danger' :
    resultado.status === 'ALERTA' ? 'warning' :
    resultado.status === 'AVISO'  ? 'warning' :
                                    'success';

  return (
    <div className='card card-custom card-stretch'>
      <div className='card-header border-0 pt-5'>
        <h3 className='card-title align-items-start flex-column'>
          <span className='card-label fw-bolder fs-3 mb-1'>
            Lançamentos com Valores "Redondos" — Detecção de Estimativas Manuais
          </span>
          <span className='text-muted mt-1 fw-bold fs-7'>
            Lei 6.404/76 Art. 177 · Resolução CFC 1.330/2011 (ITG 2000) · Lei 9.430/96 Art. 44
          </span>
        </h3>
      </div>

      <div className='card-body pt-3'>
        {resultado.status !== 'CONCILIADO' && (
          <div className={`alert alert-${bannerCor} py-3 mb-5`}>
            <strong>{resultado.status}:</strong> {resultado.mensagemDirecionamento}
          </div>
        )}

        <div className='row mb-6'>
          <div className='col-3 text-center border-end'>
            <div className='fs-1 fw-bolder text-danger'>{resultado.totalErros}</div>
            <div className='text-muted'>Erros (&gt; R$ 20.000)</div>
            <div className='text-muted fs-8'>Despesa indébita provável</div>
          </div>
          <div className='col-3 text-center border-end'>
            <div className='fs-1 fw-bolder text-warning'>{resultado.totalAlertas}</div>
            <div className='text-muted'>Alertas (R$ 5k–20k)</div>
            <div className='text-muted fs-8'>Registro por estimativa</div>
          </div>
          <div className='col-3 text-center border-end'>
            <div className='fs-1 fw-bolder text-warning'>{resultado.totalAvisos}</div>
            <div className='text-muted'>Avisos (R$ 1k–5k)</div>
            <div className='text-muted fs-8'>Sem detalhamento analítico</div>
          </div>
          <div className='col-3 text-center'>
            <div className='fs-1 fw-bolder text-danger'>{formatCurrency(resultado.passivoTributarioTotal)}</div>
            <div className='text-muted'>Passivo Tributário Total</div>
            <div className='text-muted fs-8'>IRPJ/CSLL 34% + Multa 75% + SELIC</div>
          </div>
        </div>

        {resultado.itens.length === 0 ? (
          <div className='text-success fw-bold fs-5 text-center py-8'>
            ✓ Nenhum lançamento redondo suspeito em contas de despesa operacional. Situação regular.
          </div>
        ) : (
          <div className='table-responsive'>
            <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
              <thead>
                <tr className='fw-bolder text-muted'>
                  <th>Empresa</th>
                  <th>Data</th>
                  <th>Conta Referencial</th>
                  <th>Histórico</th>
                  <th>Valor</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {resultado.itens.map((item, i) => (
                  <ItemExpandivel key={i} item={item} />
                ))}
              </tbody>
            </table>
          </div>
        )}

        <details className='mt-6'>
          <summary className='text-muted fw-bold cursor-pointer'>Base Legal</summary>
          <p className='text-muted mt-2 fs-7'>{resultado.fundamentacaoLegal}</p>
        </details>
      </div>
    </div>
  );
};

export default AuditoriaValorRedondoDetalhe;
